import React, {useCallback, useMemo, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {BulkSelector, ComboBox, FlexColumn, FlexRow, Label, Table, Text, useTableFunctions} from '@forecast-it/design-system';
import {SageSyncAnalysisTable_viewer} from './__generated__/SageSyncAnalysisTable_viewer.graphql';
import Styled from 'styled-components';
import {formatDate} from './SageSyncAnalysisPage';
import {fixIssues} from './sage-analysis-actions/FixIssues';
import {hideIssues} from './sage-analysis-actions/HideIssues';

export const ANALYSIS_COLUMNS = {
	ID: 'id',
	FORECAST_COMPANY_ID: 'forecastCompanyId',
	SAGE_ENTITY_RECORD_NUMBER: 'sageEntityRecordNumber',
	FORECAST_ENTITY_ID: 'forecastEntityId',
	ENTITY_TYPE: 'entityType',
	RELEVANCY: 'relevancy',
	COMPANY_ANALYSIS_ID: 'companyAnalysisId',
	DESCRIPTION: 'description',
};

type EntryRow = {
	id: string;
	forecastCompanyId: number | null;
	sageEntityRecordNumber: number | null;
	forecastEntityId: number | null;
	entityType: string;
	relevancy: string;
	companyAnalysisId: number;
	description: string;
};

type IssueHandler = {
	entityType: string;
	relevancy: string;
};

interface SageSyncAnalysisTableProps {
	viewer: SageSyncAnalysisTable_viewer;
	scrollRef: React.MutableRefObject<null>;
}

const TableWrapper = Styled.div`
  padding: 64px;
  padding-top: 0;
  margin-top: 64px;
  height: 100%;
`;

interface ComboItem {
	id?: string;
}

const isSupported = (row: EntryRow, supportedIssueHandlers: IssueHandler[]) => {
	for (let i = 0; i < supportedIssueHandlers.length; i++) {
		const handler = supportedIssueHandlers[i];
		if (handler.entityType === row.entityType && handler.relevancy === row.relevancy) {
			return true;
		}
	}
	return false;
};

function SageSyncAnalysisTable({viewer, scrollRef}: SageSyncAnalysisTableProps): React.JSX.Element {
	const companyAnalysis = viewer.missionControlData?.companySyncAnalysis;
	const companyId = companyAnalysis?.companyId;
	const syncDateNumber = companyAnalysis?.syncDate;
	const syncDate = syncDateNumber ? new Date(syncDateNumber) : null;
	const syncDateString = syncDate ? formatDate(new Date(syncDate)) : null;
	const entries = (companyAnalysis?.entries as EntryRow[]) || [];
	const supportedIssueHandlers = (companyAnalysis?.supportedIssueHandlers as IssueHandler[]) || [];

	const typeFilterOptions: ComboItem[] = entries
		.reduce((acc: string[], {entityType}) => {
			if (!acc.includes(entityType)) {
				acc.push(entityType);
			}
			return acc;
		}, [])
		.map(entityType => ({id: entityType}));

	const relevancyFilterOptions: ComboItem[] = entries
		.reduce((acc: string[], {relevancy}) => {
			if (!acc.includes(relevancy)) {
				acc.push(relevancy);
			}
			return acc;
		}, [])
		.map(relevancy => ({id: relevancy}));

	const [typeFilter, setTypeFilter] = useState(null);
	const [relevancyFilter, setRelevancyFilter] = useState(null);

	const filter = useMemo(() => {
		const result: string[] = [];
		if (typeFilter) {
			result.push(typeFilter);
		}
		if (relevancyFilter) {
			result.push(relevancyFilter);
		}
		return result.length > 0 ? result.join(' ') : '';
	}, [typeFilter, relevancyFilter]);

	const enableSelection = true;
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
	const onSelectionChange = useCallback((rows: EntryRow[]) => {
		setSelectedRowIds(rows.map(row => row.id));
	}, []);

	const [tableRef, table] = useTableFunctions();

	return (
		<TableWrapper>
			<FlexColumn alignItems={'baseline'}>
				<FlexRow>
					<Label>Analysis Date</Label>
					<Text>{syncDateString}</Text>
				</FlexRow>
				<FlexRow>
					<ComboBox label={'Type filter'} value={typeFilter} onChange={setTypeFilter} items={typeFilterOptions}>
						{(item: ComboItem) => (
							<ComboBox.Item textValue={item.id}>
								<Text>{item.id}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
					<ComboBox
						label={'Relevancy filter'}
						value={relevancyFilter}
						onChange={setRelevancyFilter}
						items={relevancyFilterOptions}
					>
						{(item: ComboItem) => (
							<ComboBox.Item textValue={item.id}>
								<Text>{item.id}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</FlexRow>
			</FlexColumn>
			<Table
				data-cy={'sage-sync-analysis-table'}
				data={entries}
				width={'100%'}
				stickyHeader
				tableRef={tableRef}
				getRowId={(row: EntryRow) => row.id}
				scrollRef={scrollRef}
				searchValue={filter}
				highlightSearch={true}
				enableRowSelection={row => isSupported(row.original, supportedIssueHandlers)}
				onSelectionChange={enableSelection ? onSelectionChange : undefined}
			>
				<Table.TextColumn
					id={ANALYSIS_COLUMNS.ENTITY_TYPE}
					accessorKey={'entityType'}
					data-cy={'entry-entityType'}
					header={'Entity Type'}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.TextColumn
					id={ANALYSIS_COLUMNS.RELEVANCY}
					accessorKey={'relevancy'}
					data-cy={'entry-relevancy'}
					header={'Relevancy'}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.TextColumn
					id={ANALYSIS_COLUMNS.SAGE_ENTITY_RECORD_NUMBER}
					accessorKey={'sageEntityRecordNumber'}
					data-cy={'entry-sageEntityRecordNumber'}
					header={'Sage Entity Record Number'}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.TextColumn
					id={ANALYSIS_COLUMNS.FORECAST_ENTITY_ID}
					accessorKey={'forecastEntityId'}
					data-cy={'entry-forecastEntityId'}
					header={'Forecast Entity ID'}
					allowSorting
					ellipsis
					width={'10%'}
				/>
				<Table.TextColumn
					id={ANALYSIS_COLUMNS.DESCRIPTION}
					accessorKey={'description'}
					data-cy={'entry-description'}
					header={'Description'}
					allowSorting
					ellipsis
					width={'30%'}
				/>
			</Table>
			<BulkSelector
				label={'Reports selected'}
				onClear={table.clearSelections}
				show={selectedRowIds.length > 0}
				data-cy={'bulk-selector'}
			>
				<BulkSelector.MainAction
					data-cy={'bulk-fix-issues'}
					onClick={() =>
						fixIssues(companyId, selectedRowIds, () => {
							table.clearSelections();
						})
					}
				>
					Fix issues
				</BulkSelector.MainAction>
				<BulkSelector.MainAction
					data-cy={'bulk-hide-issues'}
					onClick={() =>
						hideIssues(selectedRowIds, () => {
							table.clearSelections();
						})
					}
				>
					Hide issues (TODO)
				</BulkSelector.MainAction>
			</BulkSelector>
		</TableWrapper>
	);
}

const sageSyncAnalysisTableQuery = graphql`
	query SageSyncAnalysisTable_Query($companyId: Int, $analysisId: Int) {
		viewer {
			actualPersonId
			component(name: "sage_sync_analysis_table")
			...SageSyncAnalysisTable_viewer @arguments(companyId: $companyId, analysisId: $analysisId)
		}
	}
`;

export {sageSyncAnalysisTableQuery};

export default createFragmentContainer(SageSyncAnalysisTable, {
	viewer: graphql`
		fragment SageSyncAnalysisTable_viewer on Viewer
		@argumentDefinitions(companyId: {type: "Int"}, analysisId: {type: "Int"}) {
			id
			missionControlData {
				companySyncAnalysis(companyId: $companyId, analysisId: $analysisId) {
					id
					companyId
					syncDate
					entries {
						id
						companyAnalysisId
						forecastCompanyId
						sageEntityRecordNumber
						forecastEntityId
						entityType
						relevancy
						description
					}
					supportedIssueHandlers {
						entityType
						relevancy
					}
				}
			}
		}
	`,
});
